import React, { useState, useRef, useEffect } from "react";
import { View, StyleSheet, TouchableOpacity, ScrollView } from "react-native";
import { Text } from "react-native-paper";
import { theme } from "../theme/theme";
import Icon from "./Icon";
import { useAccessibility } from "../hooks/useAccessibility";
import { AccessibilityInfo } from "react-native";
import { useAppContext } from "../context/AppContext";
import { pickGap, hintLegDuration, EDGE_PAD } from "../utils/menuPeek";

// On every load the row drifts right and settles back, which teaches the swipe
// far better than any arrow can. The panel mounts once per page load and stays
// mounted while you move between tabs, so this plays on load and reload without
// replaying every time you change screen.
const HINT_DELAY = 800;

// Pause at the far end, so the last icons register before the row comes back.
const HINT_HOLD = 550;

/** Slow at both ends, quickest in the middle — reads as a gesture, not a jerk. */
const easeInOut = (t) =>
  t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2;

/**
 * Quick Access Icon Panel component
 * Displays frequently accessed features as horizontal icon buttons
 * Positioned above the blue header (below trial banner)
 *
 * There are more items than fit on a phone, and the row has to say so without
 * a control of its own. It does that by letting the next icon be sliced by the
 * screen edge: a cut-off shape reads as "this continues", where an arrow just
 * reads as another button.
 *
 * Where that cut lands cannot be left to chance. At a fixed 16px gap the five
 * production icons sit at 24, 115, 206, 297 and 388, so a 390px iPhone cuts the
 * fifth icon 2px in — an invisible sliver, and the fade sat right on top of it.
 * The gap is therefore computed from the measured width so the edge always
 * bisects an icon. See `pickGap` in utils/menuPeek.
 */
const QuickAccessPanel = ({
  onServicesPress,
  onJobBoardPress,
  onOffersPress,
  onCalendarPress,
  onCommunityChatPress,
  onProfilePress,
  onMyListPress,
  onContactsPress,
  onQRCodePress,
  activeTab,
  myListUnseenCount = 0,
  // The drift is worth playing once, to show what is off screen. On a phone the
  // row tucks away afterwards, so the caller turns this off on later loads
  // rather than reintroducing a menu the member already knows about.
  playHint = true,
  onHintComplete,
}) => {
  const { screenReaderEnabled, reduceMotion } = useAccessibility();
  const { showDevFeatures } = useAppContext();
  const scrollViewRef = useRef(null);
  const cancelHint = useRef(null);

  // Held in a ref so the hint effect below does not list the callback as a
  // dependency and restart the drift every time the parent re-renders.
  const hintComplete = useRef(onHintComplete);
  hintComplete.current = onHintComplete;

  // Measured rather than assumed from Dimensions: the panel is not always the
  // full window width, and guessing it left the right-hand cue stuck on.
  const [viewportWidth, setViewportWidth] = useState(0);
  const [contentWidth, setContentWidth] = useState(0);
  const [scrollX, setScrollX] = useState(0);
  const [itemWidth, setItemWidth] = useState(0);

  const announceToScreenReader = (message) => {
    if (screenReaderEnabled) {
      AccessibilityInfo.announceForAccessibility(message);
    }
  };

  const handleScroll = (event) => {
    setScrollX(event.nativeEvent.contentOffset.x);
  };

  // Map activeTab values to menu item IDs
  const getActiveMenuItemId = () => {
    const tabToIdMap = {
      "services": "services",
      "skills-needed": "job-board",
      "sponsored-listings": "offers",
      "community-calendar": "calendar",
      "community-chat": "community-chat",
      "profile": "profile",
      "my-list": "my-list",
      "contacts": "contacts",
      "qr-code": "qr-code",
    };
    return tabToIdMap[activeTab] || null;
  };

  const activeMenuItemId = getActiveMenuItemId();

  const allMenuItems = [
    {
      id: "services",
      label: "Services",
      icon: "wrench",
      onPress: onServicesPress,
      accessibilityLabel: "Services, Browse available services",
      accessibilityHint: "Navigate to services screen",
    },
    {
      id: "job-board",
      label: "Local Jobs",
      icon: "briefcase",
      onPress: onJobBoardPress,
      accessibilityLabel: "Local Jobs, Tasks neighbours need done",
      accessibilityHint: "Navigate to job board screen",
      devOnly: true,
    },
    {
      id: "offers",
      label: "Featured",
      icon: "star",
      onPress: onOffersPress,
      accessibilityLabel: "Featured, View featured listings",
      accessibilityHint: "Navigate to featured listings screen",
      devOnly: true,
    },
    {
      id: "calendar",
      label: "Events",
      icon: "calendar",
      onPress: onCalendarPress,
      accessibilityLabel: "Events, View local events",
      accessibilityHint: "Navigate to community calendar screen",
      devOnly: true,
    },
    {
      id: "community-chat",
      label: "Chat",
      icon: "chat",
      onPress: onCommunityChatPress,
      accessibilityLabel: "Community Chat, Weekly community discussion",
      accessibilityHint: "Navigate to community chat screen",
      devOnly: true,
    },
    {
      id: "profile",
      label: "Profile",
      icon: "user",
      onPress: onProfilePress,
      accessibilityLabel: "Profile, View and edit your profile",
      accessibilityHint: "Navigate to profile screen",
    },
    {
      id: "contacts",
      label: "Contacts",
      icon: "account-group",
      onPress: onContactsPress,
      accessibilityLabel: "Contacts, View your saved contacts",
      accessibilityHint: "Navigate to contacts screen",
    },
    {
      id: "my-list",
      label: "Organiser",
      icon: "check-circle",
      onPress: onMyListPress,
      accessibilityLabel: "Organiser, Shopping, tasks, dates, clocks and calendar",
      accessibilityHint: "Navigate to your organiser",
      badge: myListUnseenCount,
    },
    {
      id: "qr-code",
      label: "Invite",
      icon: "account-plus",
      onPress: onQRCodePress,
      accessibilityLabel: "Invite, Give your invite codes to people you know",
      accessibilityHint: "Navigate to your invite codes",
    },
  ];

  // Filter out dev-only items in production
  const menuItems = showDevFeatures
    ? allMenuItems
    : allMenuItems.filter(item => !item.devOnly);

  const gap = pickGap({ viewportWidth, itemWidth, itemCount: menuItems.length });

  const maxScroll = Math.max(0, contentWidth - viewportWidth);
  const hasOverflow = maxScroll > 1;
  const showLeftIndicator = scrollX > 4;
  const showRightIndicator = hasOverflow && scrollX < maxScroll - 4;

  useEffect(() => {
    // Tells the caller the row has been shown, so it can tuck it away. It must
    // fire on every path out of this effect, including the paths where no drift
    // ever runs, or a caller waiting on it would keep the menu open forever.
    const finish = () => {
      hintComplete.current?.();
      return undefined;
    };

    // Nothing to demonstrate when the row already fits, and nothing to
    // reintroduce when this member has been shown it before.
    if (!hasOverflow || !playHint) return finish();

    // Only reduceMotion is checked here, deliberately. It is tempting to skip
    // the hint for screen reader users too — motion as instruction is no use to
    // someone who cannot see it — but `screenReaderEnabled` is hardcoded to
    // true by react-native-web, which cannot detect a reader and assumes one is
    // always present. Including it disabled the drift in every browser for
    // every member, which is exactly what happened: the peek worked and the row
    // never moved, on Android, iPhone and desktop alike.
    if (reduceMotion) return finish();

    const node = scrollViewRef.current;
    if (!node) return finish();

    const leg = hintLegDuration(maxScroll);
    let frame = null;
    let timer = null;

    const stop = () => {
      if (frame) cancelAnimationFrame(frame);
      if (timer) clearTimeout(timer);
      frame = null;
      timer = null;
    };

    // If the member starts swiping we get out of the way at once, rather than
    // dragging the row back under their finger. Someone who swipes the row has
    // plainly seen it, so this counts as the introduction being done — without
    // that, taking over the drift would leave the menu open for good.
    cancelHint.current = () => {
      stop();
      finish();
    };

    const seek = (x) => node.scrollTo({ x, animated: false });

    // Driven frame by frame rather than with `animated: true`, because the
    // browser's smooth scroll picks its own duration: the same call would race
    // across a wide menu and crawl across a narrow one. Stepping it ourselves
    // keeps the row moving at one speed whatever the distance.
    const travel = (from, to, onArrive) => {
      const startedAt = performance.now();

      const step = (now) => {
        const progress = Math.min(1, (now - startedAt) / leg);
        seek(from + (to - from) * easeInOut(progress));

        if (progress < 1) frame = requestAnimationFrame(step);
        else onArrive?.();
      };

      frame = requestAnimationFrame(step);
    };

    timer = setTimeout(() => {
      travel(0, maxScroll, () => {
        timer = setTimeout(() => travel(maxScroll, 0, finish), HINT_HOLD);
      });
    }, HINT_DELAY);

    return () => {
      stop();
      cancelHint.current = null;
    };
  }, [hasOverflow, maxScroll, reduceMotion, playHint]);

  return (
    <View style={styles.panelSurface}>
      {/* Left fade — indicates more content to the left */}
      {showLeftIndicator && (
        <View style={styles.leftFade} pointerEvents="none" />
      )}

      <ScrollView
        ref={scrollViewRef}
        horizontal
        showsHorizontalScrollIndicator={false}
        contentContainerStyle={[styles.panelContent, { gap }]}
        onScroll={handleScroll}
        onTouchStart={() => cancelHint.current?.()}
        onScrollBeginDrag={() => cancelHint.current?.()}
        scrollEventThrottle={16}
        onLayout={(e) => setViewportWidth(e.nativeEvent.layout.width)}
        onContentSizeChange={(w) => setContentWidth(w)}
      >
        {menuItems.map((item, index) => {
          const isActive = item.id === activeMenuItemId;
          return (
            <TouchableOpacity
              key={item.id}
              style={[styles.iconButton, isActive && styles.iconButtonActive]}
              // The icons are a uniform width, so measuring the first is enough
              // to work out where the screen edge will fall.
              onLayout={index === 0
                ? (e) => setItemWidth(e.nativeEvent.layout.width)
                : undefined}
              onPress={() => {
                if (item.onPress) {
                  item.onPress();
                  announceToScreenReader(item.accessibilityLabel);
                }
              }}
              accessible={true}
              accessibilityRole="button"
              accessibilityLabel={item.accessibilityLabel}
              accessibilityHint={item.accessibilityHint}
              accessibilityState={{selected: isActive}}
              aria-current={isActive ? "page" : undefined}
            >
              <View style={[
                styles.iconContainer,
                isActive && styles.iconContainerActive
              ]}>
              <Icon
                name={item.icon}
                size={24}
                color={isActive ? "#000000" : theme.colors.navigationText}
                fill="none"
              />
              {item.badge && item.badge > 0 ? (
                <View style={styles.badge}>
                  <Text style={styles.badgeText} numberOfLines={1}>
                    {item.badge > 9 ? "9+" : String(item.badge)}
                  </Text>
                </View>
              ) : null}
            </View>
            <Text style={[styles.iconLabel, isActive && styles.iconLabelActive]} numberOfLines={1}>
              {item.label}
            </Text>
          </TouchableOpacity>
          );
        })}
      </ScrollView>

      {/* Right fade — indicates more content to the right */}
      {showRightIndicator && (
        <View style={styles.rightFade} pointerEvents="none" />
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  panelSurface: {
    backgroundColor: theme.colors.navigationSurface,
    width: "100%",
    paddingVertical: 0,
    // No horizontal padding here on purpose. It used to inset the ScrollView,
    // so the row was clipped 16px short of the screen and no icon could ever
    // reach the edge to be cut. The same inset now lives on the content below,
    // which keeps the first icon where it was while letting the row run right
    // up to the edge.
    position: "relative",
  },
  panelContent: {
    flexDirection: "row",
    alignItems: "stretch",
    justifyContent: "flex-start",
    paddingHorizontal: EDGE_PAD,
    // The gap is supplied inline: it is computed from the measured width so the
    // screen edge lands mid-icon. See pickGap.
  },
  iconButton: {
    alignItems: "center",
    justifyContent: "center",
    minWidth: 75,
    maxWidth: 90,
    paddingHorizontal: theme.spacing.sm,
    position: "relative",
    paddingVertical: 12,
    minHeight: 80,
    borderRadius: 0,
  },
  iconButtonActive: {backgroundColor: "transparent"},
  iconContainer: {
    justifyContent: "center",
    alignItems: "center",
    marginBottom: theme.spacing.xs,
  },
  iconContainerActive: {
    // No background circle for active state
  },
  badge: {
    position: "absolute",
    top: -2,
    right: -4,
    minWidth: 18,
    height: 18,
    borderRadius: 9,
    backgroundColor: theme.colors.error,
    paddingHorizontal: 4,
    justifyContent: "center",
    alignItems: "center",
    borderWidth: 1.5,
    borderColor: "#FAF7F2",
  },
  badgeText: {
    color: "#FFFFFF",
    fontSize: 10,
    fontWeight: "700",
    fontFamily: "Poppins",
  },
  iconLabel: {
    fontSize: 12,
    fontWeight: "500",
    color: theme.colors.navigationText,
    fontFamily: "Poppins",
    textAlign: "center",
    marginTop: 4,
  },
  iconLabelActive: {
    color: "#000000",
    fontWeight: "500",
  },
  // Short and never fully opaque. At 40px of solid background these covered the
  // sliced icon completely, which is what made the extra items invisible. They
  // now only soften the cut, so the shape behind still reads as "continues" —
  // narrower than the peek itself, so part of the sliced icon is always clean.
  rightFade: {
    position: "absolute",
    right: 0,
    top: 0,
    bottom: 0,
    width: 14,
    zIndex: 10,
    backgroundImage:
      `linear-gradient(to left, ${theme.colors.navigationSurface}D1 0%, ${theme.colors.navigationSurface}00 100%)`,
  },
  leftFade: {
    position: "absolute",
    left: 0,
    top: 0,
    bottom: 0,
    width: 14,
    zIndex: 10,
    backgroundImage:
      `linear-gradient(to right, ${theme.colors.navigationSurface}D1 0%, ${theme.colors.navigationSurface}00 100%)`,
  },
});

export default QuickAccessPanel;

